// Recent chat transcript, formatted for a prompt. The free-form answer paths (answerChat,
// answerTourAside) had NO memory — each message was answered in isolation, so follow-ups
// ("what about next year?") lost all context (#3525). This is the single builder the
// classifier and both answer paths share, so the "what counts as the current conversation"
// rule (active run's thread, else the pre-forecast draft thread) stays in one place.
import { captureChatTarget, resolveChatTarget } from "./chatTarget.js";
import { clip } from "./promptLines.js";

// How many turns of the current thread the model sees. Raised from 10: ten entries is five
// exchanges, which a single "why? / and then? / what about X?" run of follow-ups exhausts —
// the referent the classifier is told to resolve ("that", "the one I mentioned") had often
// already fallen out of the window. The whole thread is ALREADY in the run-store (the
// messages GET has no LIMIT, so opening a forecast hydrates all of it), so this is a slice
// on data in memory: widening it costs prompt tokens only, no extra fetch.
const DEFAULT_LIMIT = 20;

// Per-message ceiling. `messages.content` is capped server-side at 32 KB, so ONE pasted wall
// of text could otherwise outweigh the entire rest of the prompt — and the window is now
// twice as wide, doubling the exposure. Truncating per message keeps the transcript's cost
// proportional to the number of turns rather than to the longest one.
const DEFAULT_MAX_CHARS = 1200;

/**
 * The last `limit` non-empty entries of ONE conversation as "User:"/"Sayer:" lines: the
 * thread of `target` (a {@link ChatTarget} the caller captured where its work started — a
 * chat turn's), by default the selection captured as this is called (a run's thread when one
 * is selected, else the pre-forecast draft). Returns "" when there is nothing to show, or
 * when the target's run is gone.
 * @param {any} runState the run-store state (pass `getRunState()`)
 * @param {{ target?: import("./chatTarget.js").ChatTarget, limit?: number, maxChars?: number }} [opts]
 * @returns {string}
 */
export function recentConversationText(
  runState,
  { target, limit = DEFAULT_LIMIT, maxChars = DEFAULT_MAX_CHARS } = {},
) {
  if (!runState) return "";
  const dest = resolveChatTarget(runState, target ?? captureChatTarget(runState));
  const entries = dest ? (runState.conversationsById?.[dest.convId]?.entries ?? []) : [];
  return entries
    .slice(-limit)
    .filter((e) => e.content)
    .map((e) => `${e.role === "user" ? "User" : "Sayer"}: ${clip(e.content, maxChars)}`)
    .join("\n");
}
